import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import {
  ShoppingCart,
  Store,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
  AlertCircle,
  Crown,
  ArrowLeft,
} from "lucide-react";

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateCartItem, clearCart } = useCart();
  const { isMember, isAuthenticated } = useAuth();

  const handleQuantityChange = (item, delta) => {
    const newQuantity = item.quantity + delta;
    // Use retailer as store_id for live products
    const storeId = item.retailer || item.store_id;
    updateCartItem(item.product_id, storeId, newQuantity);
  };

  const handleRemoveItem = (item) => {
    // Use retailer as store_id for live products
    const storeId = item.retailer || item.store_id;
    updateCartItem(item.product_id, storeId, 0);
  };

  if (cart.items?.length === 0) {
    return (
      <div className="container-main py-16 text-center" data-testid="empty-cart">
        <ShoppingCart className="w-20 h-20 text-slate-300 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">
          Your Cart is Empty
        </h1>
        <p className="text-slate-600 mb-8">
          Start adding products to compare prices and save money!
        </p>
        <Button onClick={() => navigate("/products")} data-testid="browse-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Browse Products
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cart-page">
      <div className="container-main py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">
            Shopping Cart
          </h1>
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={clearCart}
            data-testid="clear-cart-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Cart
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Store Count Info */}
            {cart.store_count > 1 && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-sky-600" />
                  <div>
                    <p className="font-medium text-slate-900">
                      This order will be fulfilled from {cart.store_count} stores
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Items by Store */}
            {cart.stores?.map((store) => (
              <div key={store.store_id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        store.store_id === 'coles' ? 'bg-red-600' : 
                        store.store_id === 'woolworths' ? 'bg-green-600' : 'bg-slate-900'
                      }`}>
                        {(store.store_name || store.store_id || 'S').charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{store.store_name || store.store_id || 'Store'}</span>
                    </div>
                    <span className="text-sm text-slate-600">
                      Subtotal: ${(store.subtotal || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  {store.items?.map((item, index) => (
                    <div
                      key={`${item.product_id}-${item.retailer || item.store_id || index}`}
                      className="flex gap-4 p-4"
                      data-testid={`cart-item-${index}`}
                    >
                      <img
                        src={item.image_url || "https://images.unsplash.com/photo-1542838132-92c53300491e?w=100&h=100&fit=crop"}
                        alt={item.product_name}
                        className="w-20 h-20 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://images.unsplash.com/photo-1542838132-92c53300491e?w=100&h=100&fit=crop";
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="font-medium text-slate-900">{item.product_name}</h3>
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`remove-${index}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border rounded-lg">
                            <button
                              onClick={() => handleQuantityChange(item, -1)}
                              className="p-2 hover:bg-slate-100 rounded-l-lg"
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="px-4 font-medium">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(item, 1)}
                              className="p-2 hover:bg-slate-100 rounded-r-lg"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="text-right">
                            <p className="font-bold text-slate-900">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                            <p className="text-sm text-slate-500">
                              ${item.price.toFixed(2)} each
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                Order Summary
              </h2>

              {/* Fee Notice */}
              {!isAuthenticated && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Guest Checkout</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        7% convenience fee applies. <button onClick={() => navigate("/?auth=signup")} className="text-sky-600 hover:text-sky-700 font-semibold underline" data-testid="signup-link">Sign up</button> as a member for 4% fee!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isMember && (
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-sky-600" />
                    <span className="text-sm font-medium text-sky-800">Member discount applied (4%)</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal ({cart.items?.length} items)</span>
                  <span className="font-medium">${cart.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">
                    Convenience Fee ({isMember ? "4%" : "7%"})
                  </span>
                  <span className="font-medium">${cart.convenience_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Delivery Fee</span>
                  <span>$10.00</span>
                </div>

                <Separator />

                <div className="flex justify-between text-lg">
                  <span className="font-bold">Estimated Total</span>
                  <span className="font-bold">${cart.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Fee Disclaimer */}
              <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600">
                  <strong>Fee Information:</strong> A convenience fee of <strong>4% for Members</strong> or <strong>7% for Guests</strong> applies to all orders, capped at a <strong>maximum of $15</strong>. Delivery fee is a flat <strong>$10</strong>.
                </p>
              </div>

              <Button
                className="w-full mt-6 h-12 btn-accent"
                onClick={() => navigate("/checkout")}
                data-testid="proceed-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={() => navigate("/products")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
